import { replaceImageDomain } from './replaceImageDomain';

export type ImageSize = "small" | "medium" | "large" | "xlarge";

export interface TransformConfig {
  width: number;
  quality: number;
}

export const IMAGE_TRANSFORM_PRESETS: Record<ImageSize, TransformConfig> = {
  small: { width: 200, quality: 80 }, // Avatars/thumbnails
  medium: { width: 400, quality: 80 }, // Grid cards, mobile heroes
  large: { width: 800, quality: 85 }, // Tablet heroes
  xlarge: { width: 1600, quality: 85 }, // Desktop full-width heroes
};

/**
 * Apply Cloudflare Image Transform to an R2 image URL or static asset
 * Returns original URL if not transformable or if URL is null
 *
 * Supports:
 * - R2-hosted images (r2.whatthefuckareyouwearing.com)
 * - Static assets served through Cloudflare Pages (relative paths like /images/...)
 *
 * @param url - Image URL to transform (can be relative path or absolute URL)
 * @param size - Target size preset (small, medium, large, xlarge)
 * @param r2Domain - R2 domain to check against (default: r2.whatthefuckareyouwearing.com)
 * @returns Transformed Cloudflare URL or original URL
 *
 * @example
 * transformImage('https://r2.example.com/image.jpg', 'medium')
 * // Returns: 'https://r2.example.com/cdn-cgi/image/width=400,quality=80,format=webp/https://r2.example.com/image.jpg'
 *
 * @example
 * transformImage('/images/try-on.jpg', 'small')
 * // Returns: 'https://yourdomain.com/cdn-cgi/image/width=200,quality=80,format=webp/https://yourdomain.com/images/try-on.jpg'
 */
export function transformImage(
  url: string | null | undefined,
  size: ImageSize,
  r2Domain = "r2.whatthefuckareyouwearing.com"
): string | null {
  // Handle null/undefined URLs
  if (!url) return null;

  // Replace domain in the URL first
  const urlWithReplacedDomain = replaceImageDomain(url);

  const config = IMAGE_TRANSFORM_PRESETS[size];
  let absoluteUrl: string;
  let domain: string;

  try {
    // Check if URL is relative (starts with /)
    if (urlWithReplacedDomain.startsWith("/")) {
      // Convert relative path to absolute URL for Cloudflare Pages static assets
      if (typeof window === "undefined") {
        // Server-side rendering: return original URL (can't determine origin)
        return urlWithReplacedDomain;
      }
      absoluteUrl = `${window.location.origin}${urlWithReplacedDomain}`;
      domain = window.location.origin;
    } else if (urlWithReplacedDomain.includes(r2Domain) || urlWithReplacedDomain.includes("r2.wtf.style")) {
      // R2-hosted image (check both old and new domain)
      const urlObj = new URL(urlWithReplacedDomain);
      absoluteUrl = urlWithReplacedDomain;
      domain = `${urlObj.protocol}//${urlObj.host}`;
    } else {
      // External URL or non-transformable URL - return as-is with domain replaced
      return urlWithReplacedDomain;
    }

    const params = `width=${config.width},quality=${config.quality},format=webp`;
    return `${domain}/cdn-cgi/image/${params}/${absoluteUrl}`;
  } catch (error) {
    // If URL parsing fails, return original URL with domain replaced
    console.warn("Failed to parse URL for image transform:", urlWithReplacedDomain, error);
    return urlWithReplacedDomain;
  }
}

/**
 * Build srcSet string for responsive images with Cloudflare transforms
 * @param url - Base image URL
 * @param sizes - Array of size presets to include
 * @returns srcSet string like "url1 400w, url2 800w, url3 1600w"
 *
 * @example
 * buildTryOnSrcSet('https://r2.example.com/image.jpg', ['medium', 'large', 'xlarge'])
 * // Returns: 'https://r2.example.com/cdn-cgi/image/width=400.../image.jpg 400w, ...'
 */
export function buildTryOnSrcSet(url: string | null | undefined, sizes: ImageSize[]): string {
  if (!url) return "";

  return sizes
    .map((size) => {
      const transformedUrl = transformImage(url, size);
      const width = IMAGE_TRANSFORM_PRESETS[size].width;
      return `${transformedUrl} ${width}w`;
    })
    .join(", ");
}

/**
 * Convenience functions for common image sizes
 */
export const transformSmall = (url: string | null | undefined) => transformImage(url, "small");
export const transformMedium = (url: string | null | undefined) => transformImage(url, "medium");
export const transformLarge = (url: string | null | undefined) => transformImage(url, "large");
export const transformXLarge = (url: string | null | undefined) => transformImage(url, "xlarge");
